import axios from 'axios'
import { CaseworkerActionType, LineOfContactActionType } from '../action-types'
import config from '../../config'
import { Dispatch } from 'redux'
import {
    ApiOrganization,
    ApiIncidentPreview,
    mapPreviewToListItem,
} from '../../ts/api-types'
import { isAxiosError } from '../../ts/axiosError'
import { setCaseworkerLoading, setCaseworkerError } from './caseworkers'
import { FlashMessageInterface } from '../reducers/lineOfContactReducer'
import { UpdateContactInterface } from './lineOfContacts'
import Translation from '../state-types/TranslationStateType'

const showFlashMessage = (message: FlashMessageInterface) => ({
    type: LineOfContactActionType.SHOW_FLASH_MESSAGE,
    payload: message,
})

interface ApiListResponse<T> {
    data: T[]
}

const { apiUrl } = config

export const getOrganizations = (id: number) => async (dispatch: Dispatch) => {
    dispatch(setCaseworkerLoading())

    try {
        const res = await axios.get<ApiListResponse<ApiOrganization>>(
            `${apiUrl}/v1/partners/${id}/clients`
        )

        if (res.status !== 201 && res.status !== 200 && res.status !== 204) {
            return dispatch(
                setCaseworkerError(
                    'Something went wrong fetching the clients please try again later'
                )
            )
        }

        return dispatch({
            type: CaseworkerActionType.GET_ORGANIZATIONS,
            payload: res.data.data,
        })
    } catch (error) {
        return dispatch(
            setCaseworkerError(
                'Something went wrong fetching the clients please try agin later'
            )
        )
    }
}

export const getAllReports = () => async (dispatch: Dispatch) => {
    dispatch(setCaseworkerLoading())

    try {
        const res = await axios.get<ApiListResponse<ApiIncidentPreview>>(
            `${apiUrl}/v1/me/incidents?status=new,action+required,awaiting+response`
        )

        if (res.status !== 201 && res.status !== 200 && res.status !== 204) {
            return dispatch(
                setCaseworkerError('Something went wrong fetching the reports')
            )
        }

        return dispatch({
            type: CaseworkerActionType.GET_ALL_REPORTS,
            payload: res.data.data.map(mapPreviewToListItem),
        })
    } catch (error) {
        return dispatch(
            setCaseworkerError('Something went wrong fetching the reports')
        )
    }
}

export const getPartnerClosedReports =
    () => async (dispatch: Dispatch) => {
        dispatch(setCaseworkerLoading())

        try {
            const res = await axios.get<ApiListResponse<ApiIncidentPreview>>(
                `${apiUrl}/v1/me/incidents?status=solved,rejected,deleted,archived&with=trashed`
            )

            if (
                res.status !== 201 &&
                res.status !== 200 &&
                res.status !== 204
            ) {
                return dispatch(
                    setCaseworkerError(
                        'Something went wrong fetching the reports'
                    )
                )
            }

            return dispatch({
                type: CaseworkerActionType.GET_CLOSED_REPORTS,
                payload: res.data.data.map(mapPreviewToListItem),
            })
        } catch (error) {
            return dispatch(
                setCaseworkerError('Something went wrong fetching the reports')
            )
        }
    }

export const getClientInformation =
    (partnerId: number, id: number) => async (dispatch: Dispatch) => {
        dispatch(setCaseworkerLoading())

        try {
            const res = await axios.get(
                `${apiUrl}/v1/partners/${partnerId}/clients/${id}?with=contact_person,incidents,contact_lines`
            )

            if (res.status !== 200) {
                return dispatch(
                    setCaseworkerError('Something went wrong please reload')
                )
            }

            dispatch({
                type: CaseworkerActionType.GET_CLIENT_INFORMATION,
                payload: res.data.data,
            })

            dispatch({
                type: LineOfContactActionType.GET_LINE_OF_CONTACTS,
                payload: res.data.data.contact_lines.data,
            })
        } catch (error) {
            return dispatch(
                setCaseworkerError('Something went wrong please reload')
            )
        }
    }

export const getSelfOrganization = () => async (dispatch: Dispatch) => {
    dispatch(setCaseworkerLoading())

    try {
        const res = await axios.get(
            `${apiUrl}/v1/partner/partners/organization`
        )

        if (res.status !== 200) {
            return dispatch(
                setCaseworkerError('Something went wrong please reload')
            )
        }

        return dispatch({
            type: CaseworkerActionType.GET_SELF_ORGANIZATION,
            payload: res.data.payload,
        })
    } catch (error) {
        return dispatch(
            setCaseworkerError('Something went wrong please reload')
        )
    }
}

export const addCaseworker =
    (partnerId: number, payload: any) => async (dispatch: Dispatch) => {
        dispatch(setCaseworkerLoading())

        try {
            const res = await axios.post(
                `${apiUrl}/v1/partners/${partnerId}/caseworkers`,
                payload
            )

            if (res.status !== 201) {
                if (typeof res.data.data.errors !== 'undefined') {
                    dispatch({
                        type: CaseworkerActionType.SHOW_CREATE_CASEWORKER_VALIDATION_ERRORS,
                        payload: res.data.data.errors,
                    })
                } else {
                    dispatch(
                        setCaseworkerError('Something went wrong please reload')
                    )
                }

                return false
            }

            const user = res.data.data

            dispatch({
                type: CaseworkerActionType.ADD_CASEWORKER,
                payload: user,
            })

            return true
        } catch (error: unknown) {
            if (
                isAxiosError(error) &&
                error.response?.data?.errors !== undefined
            ) {
                dispatch({
                    type: CaseworkerActionType.SHOW_CREATE_CASEWORKER_VALIDATION_ERRORS,
                    payload: error.response.data.errors,
                })
            } else {
                dispatch(
                    setCaseworkerError('Something went wrong please reload')
                )
            }

            return false
        }
    }

export const getAllCaseworkers =
    (partnerId: number) => async (dispatch: Dispatch) => {
        dispatch({
            type: CaseworkerActionType.SET_FETCH_CASEWORKERS_LOADING,
        })

        try {
            const res = await axios.get(
                `${apiUrl}/v1/partners/${partnerId}/caseworkers/`
            )

            if (res.status !== 200) {
                return dispatch(
                    setCaseworkerError('Something went wrong please reload')
                )
            }

            return dispatch({
                type: CaseworkerActionType.GET_ALL_CASEWORKERS,
                payload: res.data.data,
            })
        } catch (error) {
            return dispatch(
                setCaseworkerError('Something went wrong please reload')
            )
        }
    }

export const getSingleCaseworker =
    (id: number) => async (dispatch: Dispatch) => {
        dispatch(setCaseworkerLoading())

        try {
            const res = await axios.get(`${apiUrl}/v1/users/${id}`)

            if (res.status !== 200) {
                return dispatch(
                    setCaseworkerError('Something went wrong please reload')
                )
            }

            return dispatch({
                type: CaseworkerActionType.GET_SINGLE_CASEWORKER,
                payload: res.data.data,
            })
        } catch (error) {
            return dispatch(
                setCaseworkerError('Something went wrong please reload')
            )
        }
    }

export const addCaseworkerValidation =
    (user: any) => async (dispatch: Dispatch) => {
        dispatch(setCaseworkerLoading())

        const payload = {
            name: user.name,
            first_name: 'X',
            last_name: 'X',
            phone: user.phone,
            email: user.email,
        }

        try {
            const res = await axios.post(
                `${apiUrl}/v1/auth/validate-personal-information`,
                payload
            )
            if (
                res.status !== 201 &&
                res.status !== 200 &&
                res.status !== 204
            ) {
                dispatch({
                    type: CaseworkerActionType.ADD_CASEWORKER_VALIDATION,
                    payload: res.data.errors,
                })

                return false
            }

            return true
        } catch (error: unknown) {
            dispatch({
                type: CaseworkerActionType.ADD_CASEWORKER_VALIDATION,
                payload: isAxiosError(error)
                    ? error.response?.data?.errors
                    : undefined,
            })
            return false
        }
    }

export const clearCaseworkerValidationErrors = () => ({
    type: CaseworkerActionType.CLEAR_CASEWORKER_VALIDATION_ERRORS,
})

export const getPartnerUsers =
    (partnerId: number) => async (dispatch: Dispatch) => {
        try {
            const res = await axios.get(
                `${apiUrl}/v1/partners/${partnerId}/user-management`
            )
            dispatch({
                type: CaseworkerActionType.GET_ALL_CASEWORKERS,
                payload: res.data.data,
            })
        } catch (error) {
            // silently fail
        }
    }

export const invitePartnerUser =
    (partnerId: number, email: string, roles: string[]) =>
    async (_dispatch: Dispatch) => {
        try {
            const res = await axios.post(
                `${apiUrl}/v1/partners/${partnerId}/user-management/invite`,
                { email, roles }
            )
            return res.status === 204 || res.status === 200
        } catch (error) {
            return false
        }
    }

export const revokePartnerUser =
    (partnerId: number, userId: number) => async (_dispatch: Dispatch) => {
        try {
            const res = await axios.post(
                `${apiUrl}/v1/partners/${partnerId}/user-management/${userId}/revoke`
            )
            return res.status === 204 || res.status === 200
        } catch (error) {
            return false
        }
    }

export const deletePartnerUser =
    (partnerId: number, userId: number) => async (_dispatch: Dispatch) => {
        try {
            const res = await axios.delete(
                `${apiUrl}/v1/partners/${partnerId}/user-management/${userId}`
            )
            return res.status === 204 || res.status === 200
        } catch (error: unknown) {
            if (isAxiosError(error) && error.response?.status === 409) {
                return {
                    error:
                        error.response.data?.message ||
                        'Cannot delete user with open incidents.',
                }
            }
            return false
        }
    }

export const assignCaseworkerToOrganization =
    (partnerId: number, organizationId: number, userId: number) =>
    async (dispatch: Dispatch) => {
        try {
            const res = await axios.post(
                `${apiUrl}/v1/partners/${partnerId}/organizations/${organizationId}/caseworkers`,
                { user_id: userId }
            )

            if (res.status === 201) {
                dispatch({
                    type: LineOfContactActionType.GET_LINE_OF_CONTACTS,
                    payload: res.data.data,
                })
                return true
            }

            return false
        } catch (error) {
            return false
        }
    }

export const updatePartnerContact =
    (
        partnerId: number,
        organizationId: number,
        contactId: number,
        contact: UpdateContactInterface,
        translation: Translation
    ) =>
    async (dispatch: Dispatch) => {
        dispatch({
            type: LineOfContactActionType.SET_LINE_OF_CONTACT_UPDATE_LOADING,
        })

        const payload = new FormData()
        payload.append('_method', 'PUT')

        Object.entries(contact.contact).forEach(([key, value]) => {
            payload.append(`contact[${key}]`, value.toString())
        })

        Object.entries(contact.contact_information).forEach(([key, value]) => {
            if (key === 'profile_img') {
                payload.append(`contact_information[${key}]`, value as File)
            } else {
                payload.append(`contact_information[${key}]`, value.toString())
            }
        })

        contact.languages.data.forEach((language, index) => {
            payload.append(
                `languages[${index}][language_id]`,
                language.language_id.toString()
            )
        })

        try {
            const res = await axios.post(
                `${apiUrl}/v1/partners/${partnerId}/organizations/${organizationId}/caseworkers/${contactId}`,
                payload
            )

            if (res.status === 200) {
                dispatch({
                    type: LineOfContactActionType.UPDATE_CONTACT_LIST_IN_STATE,
                    payload: res.data.data,
                })

                dispatch(
                    showFlashMessage({
                        message: `${translation.singleClientViewPartner.contactUpdated}`,
                        type: 'success',
                    })
                )

                return true
            }

            return false
        } catch (error: unknown) {
            if (isAxiosError(error) && error.response?.status === 422) {
                dispatch({
                    type: LineOfContactActionType.SHOW_UPDATE_CONTACT_ERRORS,
                    payload: error.response.data,
                })
            }
            return false
        }
    }

export const removeCaseworkerFromOrganization =
    (partnerId: number, organizationId: number, userId: number) =>
    async (dispatch: Dispatch) => {
        dispatch({
            type: LineOfContactActionType.SET_LINE_OF_CONTACT_DELETE_LOADING,
        })

        try {
            const res = await axios.delete(
                `${apiUrl}/v1/partners/${partnerId}/organizations/${organizationId}/caseworkers/${userId}`
            )

            if (res.status === 204) {
                dispatch({
                    type: LineOfContactActionType.REMOVE_CONTACT_FROM_STATE,
                    payload: userId,
                })
                return true
            }

            return false
        } catch (error: unknown) {
            if (isAxiosError(error) && error.response?.status === 409) {
                dispatch({
                    type: LineOfContactActionType.SHOW_FLASH_MESSAGE,
                    payload: {
                        type: 'error',
                        message:
                            error.response.data?.message ||
                            'Cannot remove caseworker with open incidents in this organization.',
                    },
                })
            }

            dispatch({
                type: LineOfContactActionType.RESET_LINE_OF_CONTACT_LOADING,
            })

            return false
        }
    }
